import { Headline, Text } from '@oneaudi/unified-web-components';
import React from 'react';
import { CECLabelFigure, EfficiencyDisplayContainer } from './ConsumptionEmissionLayerPlp.styles';
import { SYS_SPACE_RELATIVE_LG, SYS_SPACE_RELATIVE_MD } from '@oneaudi/unified-web-common';
import { useServicesContext, VehicleBasic } from '@oneaudi/vtp-shared';
import { LocaleServiceV1 } from '@volkswagen-onehub/locale-service';
import { useI18n } from '@oneaudi/i18n-context';

interface EfficiencyDisplayProps {
  vehicle: VehicleBasic;
  eecImageUrl: string;
  useEfficiencyImage: boolean;
}
export const EfficiencyDisplay: React.FC<EfficiencyDisplayProps> = ({
  vehicle,
  eecImageUrl,
  useEfficiencyImage,
}) => {
  const localeService = useServicesContext().getAdditionalService(
    'gfa:locale-service'
  ) as LocaleServiceV1;
  const country = localeService?.countryCode?.toLowerCase();
  const isFrenchMarket = country === 'fr';
  const showEmissionRepresentationAccordingToCOC6 = vehicle.noNedc;
  const modifiedEECImgURL = eecImageUrl ?? '';
  const TextKeys = {
    headline: useI18n({
      id: 'nemo.ui.sc.emission.headline',
      defaultMessage: '',
    }),
    disclaimer: useI18n({
      id: 'nemo.ui.sc.efficiencyImageDisclaimer',
      defaultMessage: '',
    }),
  };

  const getEfficiencyClass = (): string => {
    if (!vehicle) {
      return '';
    }
    if (vehicle.efficiencyClass) {
      // CH market
      return vehicle.efficiencyClass;
    }
    const vlsEfficiency = vehicle.vlsEfficiencyClass?.code;
    if (vlsEfficiency) {
      return vlsEfficiency;
    }
    const envkvIODataEfficiency = vehicle.envkvIOData?.efficiencyClass;
    if (envkvIODataEfficiency) {
      return envkvIODataEfficiency;
    }
    // vehicle has not efficiency class
    return '';
  };
  const efficiencyClass = getEfficiencyClass();
  const encodedEfficiencyClass = encodeURIComponent(efficiencyClass.toLowerCase());

  const getEfficiencyImageURL = (): string => {
    if (isFrenchMarket) {
      return vehicle.eecLabel ?? '';
    }
    let imagePath = `${modifiedEECImgURL}/img/eec/${country}/eec-label-`;
    const imageURL = showEmissionRepresentationAccordingToCOC6
      ? (imagePath += 'generic.svg')
      : (imagePath += `${encodedEfficiencyClass}.svg`);
    return imageURL;
  };
  const efficiencyImageUrl = getEfficiencyImageURL();
  return efficiencyImageUrl && useEfficiencyImage ? (
    <EfficiencyDisplayContainer>
      <Headline
        tag="h4"
        variant={{
          order: '4',
          type: 'headline',
          weight: 'normal',
        }}
        margin={[0, 0, SYS_SPACE_RELATIVE_LG, 0]}
      >
        {TextKeys.headline}
      </Headline>
      <CECLabelFigure
        imageUrl={efficiencyImageUrl}
        data-testid="itemValue_eecClassImage"
        aria-label={
          (efficiencyImageUrl ?? '')
            .split('/')
            .pop()
            ?.replace(/\..+$/, '')
            .replace(/[-_]/g, ' ')
            .replace(/\b\w/g, (c) => c.toUpperCase()) ?? ''
        }
      />
      {TextKeys.disclaimer.length > 0 && (
        <div style={{ width: '294px' }}>
          <Text
            tag="p"
            variant={{
              order: '3',
              style: 'normal',
            }}
            margin={[SYS_SPACE_RELATIVE_MD, 0, 0, 0]}
          >
            {TextKeys.disclaimer}
          </Text>
        </div>
      )}
    </EfficiencyDisplayContainer>
  ) : null;
};
